import { createStore, createStateHook, createActionsHook } from 'react-sweet-state';
import type {
	BoundActions,
	HookActionsFunction,
	HookStateFunction,
	Store,
	StoreActionApi,
} from 'react-sweet-state';

/**
 * Store for external visibility control of OmniBar.
 *
 * Uses a counter-based approach to handle multiple components requesting
 * to hide OmniBar simultaneously. The OmniBar is hidden when hideRequestCount > 0.
 *
 * Usage example (in external component):
 *   const { requestHideOmniBar, releaseHideOmniBar } = useOmniBarVisibilityActions();
 *
 *   useLayoutEffect(() => {
 *     requestHideOmniBar();
 *     return () => releaseHideOmniBar();
 *   }, [requestHideOmniBar, releaseHideOmniBar]);
 */

type OmniBarVisibilityState = {
	// Count of active hide requests. OmniBar hides when > 0
	hideRequestCount: number;
	// Becomes true on the first requestHideOmniBar call and never resets.
	// The OmniBar reads this to enable CSS transitions before the first hover,
	// so that programmatic hides (inline comments, highlight actions, sidebar
	// flyout) still animate instead of vanishing instantly.
	hasReceivedHideRequest: boolean;
};

const initialState: OmniBarVisibilityState = {
	hideRequestCount: 0,
	hasReceivedHideRequest: false,
};

const actions = {
	/**
	 * Request to hide the OmniBar. Call this when your component/popup opens.
	 * Must be paired with releaseHideOmniBar when the component closes.
	 */
	requestHideOmniBar:
		() =>
		({ setState, getState }: StoreActionApi<OmniBarVisibilityState>): void => {
			const { hideRequestCount } = getState();
			setState({
				hideRequestCount: hideRequestCount + 1,
				hasReceivedHideRequest: true,
			});
		},

	/**
	 * Release a hide request. Call this when your component/popup closes.
	 * Must be paired with a previous requestHideOmniBar call.
	 */
	releaseHideOmniBar:
		() =>
		({ setState, getState }: StoreActionApi<OmniBarVisibilityState>): void => {
			const { hideRequestCount } = getState();
			if (hideRequestCount > 0) {
				setState({ hideRequestCount: hideRequestCount - 1 });
			}
		},
};

export type OmniBarVisibilityStoreActions = typeof actions;

export const OmniBarVisibilityStore: Store<
	OmniBarVisibilityState,
	{
		/**
		 * Request to hide the OmniBar. Call this when your component/popup opens.
		 * Must be paired with releaseHideOmniBar when the component closes.
		 */
		requestHideOmniBar: () => ({
			setState,
			getState,
		}: StoreActionApi<OmniBarVisibilityState>) => void;
		/**
		 * Release a hide request. Call this when your component/popup closes.
		 * Must be paired with a previous requestHideOmniBar call.
		 */
		releaseHideOmniBar: () => ({
			setState,
			getState,
		}: StoreActionApi<OmniBarVisibilityState>) => void;
	}
> = createStore({
	initialState,
	actions,
	name: 'OmniBarVisibilityStore',
});

/**
 * Hook to check if OmniBar should be hidden.
 * Returns true when any component has requested to hide OmniBar.
 * Uses selector-based subscription - only re-renders when this value changes.
 */
export const useShouldHideOmniBar: HookStateFunction<boolean, void> = createStateHook(
	OmniBarVisibilityStore,
	{
		selector: (state: OmniBarVisibilityState) => state.hideRequestCount > 0,
	},
);

/**
 * Hook that returns true once any component has ever called requestHideOmniBar.
 * The OmniBar uses this to enable CSS transitions before the user's first hover
 * so programmatic hides animate correctly.
 */
export const useHasReceivedOmniBarHideRequest: HookStateFunction<boolean, void> = createStateHook(
	OmniBarVisibilityStore,
	{
		selector: (state: OmniBarVisibilityState) => state.hasReceivedHideRequest,
	},
);

/**
 * Hook to get OmniBar visibility actions.
 * Use this in external components to control OmniBar visibility.
 */
export const useOmniBarVisibilityActions: HookActionsFunction<
	BoundActions<
		OmniBarVisibilityState,
		{
			/**
			 * Request to hide the OmniBar. Call this when your component/popup opens.
			 * Must be paired with releaseHideOmniBar when the component closes.
			 */
			requestHideOmniBar: () => ({
				setState,
				getState,
			}: StoreActionApi<OmniBarVisibilityState>) => void;
			/**
			 * Release a hide request. Call this when your component/popup closes.
			 * Must be paired with a previous requestHideOmniBar call.
			 */
			releaseHideOmniBar: () => ({
				setState,
				getState,
			}: StoreActionApi<OmniBarVisibilityState>) => void;
		}
	>
> = createActionsHook(OmniBarVisibilityStore);
